import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useAiCoverGeneration } from '@/hooks/use-ai-cover-generation';
import { MAX_AI_COVER_PROMPT_LENGTH, resolveAiCoverRequest } from '@/lib/cover/resolveAiCoverRequest';
import type { AiCoverErrorCode } from '@/lib/cover/mapAiCoverError';
import { FrameSourcePicker } from '@/components/cover/FrameSourcePicker';
import { AI_COVER_PROMPT_PRESETS } from '@/lib/cover/aiCoverPromptPresets';
import { hasAiCoverPromptPreset, toggleAiCoverPromptPreset } from '@/lib/cover/toggleAiCoverPromptPreset';
import { useCoverFrameCandidates } from '@/hooks/use-cover-frame-candidates';
import type { CoverFrameSource } from '@/lib/cover/buildPreviewStillInputProps';
import { resolveFrameSourceSelection } from '@/lib/cover/resolveFrameSourceSelection';

export type AiCoverDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The saveable clip the generation is attached to (ownership is checked
   *  server-side). */
  clipId: string | null;
  /** Pre-fills the prompt. */
  clipTitle: string | null;
  /** width/height of the export; picks portrait/landscape and the result box. */
  exportAspect: number;
  /** Whether a reference frame can be captured (a source video exists).
   *  Without one the reference toggle is hidden and generation is
   *  prompt-only. */
  referenceAvailable: boolean;
  /** The frame as the preview draws it (layout, subtitles, layers). */
  loadPreviewFrame?: () => Promise<string | null>;
  /** The raw source-video frame at `time`, cropped to the export aspect. */
  loadVideoFrame: (time: number) => Promise<string | null>;
  /** Uploads the chosen reference frame (the same path as "Usar frame
   *  atual", without registering it in My Media). Null on failure. */
  uploadReference: (dataUrl: string, captureTime: number) => Promise<string | null>;
  /** Source-video playhead when the dialog opened, in seconds. */
  currentTime: number;
  /** Called with the generated image URL when the user picks it. */
  onUseCover: (url: string) => void;
};

/** "Gerar com IA": prompt (+ optional current-frame reference — the preview
 *  frame or the raw video frame, picked side by side) → one image,
 *  generated asynchronously by the worker and polled by `useAiCoverGeneration`.
 *  Mount it only while open — unmounting stops the polling. */
export function AiCoverDialog({
  open,
  onOpenChange,
  clipId,
  clipTitle,
  exportAspect,
  referenceAvailable,
  loadPreviewFrame,
  loadVideoFrame,
  uploadReference,
  currentTime,
  onUseCover,
}: AiCoverDialogProps) {
  const { t } = useTranslation();
  const promptId = useId();
  const referenceId = useId();
  const { state, quota, refetchQuota, generate, reset } = useAiCoverGeneration();
  const [prompt, setPrompt] = useState(clipTitle ?? '');
  const [useReference, setUseReference] = useState(referenceAvailable);
  const [preparing, setPreparing] = useState(false);
  const [localError, setLocalError] = useState<AiCoverErrorCode | null>(null);
  // Snapshotted on open: the reference candidates belong to this playhead.
  const [referenceTime] = useState(currentTime);
  const [chosenSource, setChosenSource] = useState<CoverFrameSource | null>(null);
  const { candidates, retry: retryCandidate } = useCoverFrameCandidates(open && referenceAvailable && useReference, {
    loadPreviewFrame,
    loadVideoFrame: () => loadVideoFrame(referenceTime),
  });
  const referenceSource = resolveFrameSourceSelection(chosenSource, candidates);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) void refetchQuota();
  }, [open, refetchQuota]);

  const generating = preparing || state.status === 'generating';
  // The selected reference is still rendering: wait for it rather than fail.
  const referencePending =
    referenceAvailable && useReference && referenceSource !== null && candidates[referenceSource].status === 'loading';
  const errorCode: AiCoverErrorCode | null =
    localError ?? (state.status === 'error' ? state.error ?? 'unknown' : null);

  const handleGenerate = async () => {
    if (generating || referencePending) return;
    setLocalError(null);
    reset();

    // The backend answers limit 0 when it does not see the viewer as Pro —
    // explain the plan, not a "daily limit" at 0/0.
    if (quota && quota.limit === 0) {
      setLocalError('pro_required');
      return;
    }
    if (quota && quota.used >= quota.limit) {
      setLocalError('daily_limit_reached');
      return;
    }
    const request = resolveAiCoverRequest({
      prompt,
      useReference: referenceAvailable && useReference,
      exportAspect,
    });
    if (request.ok === false) {
      setLocalError(request.code);
      return;
    }
    if (!clipId) {
      setLocalError('not_found');
      return;
    }

    let referenceUrl: string | null = null;
    if (request.value.useReference) {
      const candidate = referenceSource ? candidates[referenceSource] : null;
      if (candidate?.status !== 'ready') {
        // Still rendering (or both failed): nothing to send yet.
        setLocalError('invalid_reference');
        return;
      }
      setPreparing(true);
      try {
        referenceUrl = await uploadReference(candidate.dataUrl, referenceTime);
      } catch {
        referenceUrl = null;
      }
      if (!mountedRef.current) return;
      setPreparing(false);
      if (!referenceUrl) {
        setLocalError('invalid_reference');
        return;
      }
    }

    await generate({
      clipId,
      prompt: request.value.prompt || null,
      referenceUrl,
      aspect: request.value.aspect,
    });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const resultUrl = state.status === 'done' ? state.resultUrl : undefined;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="ai-cover-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
            {t('editor.coverImage.ai.title')}
          </DialogTitle>
          {quota && quota.limit > 0 && (
            <DialogDescription>
              {t('editor.coverImage.ai.quota', { used: quota.used, limit: quota.limit })}
            </DialogDescription>
          )}
        </DialogHeader>

        {resultUrl ? (
          <div className="space-y-3">
            <div className="flex justify-center rounded-md bg-muted/40 p-2">
              <div
                data-testid="ai-cover-result"
                className="relative w-full max-w-[200px] overflow-hidden rounded-md border border-border bg-black"
                style={{ aspectRatio: String(exportAspect) }}
              >
                <img
                  src={resultUrl}
                  alt={t('editor.coverImage.previewAlt')}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={referencePending}
                onClick={() => void handleGenerate()}
              >
                <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden="true" />
                {t('editor.coverImage.ai.generateAnother')}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  onUseCover(resultUrl);
                  handleOpenChange(false);
                }}
              >
                {t('editor.coverImage.ai.useAsCover')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {referenceAvailable && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Switch
                    id={referenceId}
                    checked={useReference}
                    disabled={generating}
                    onCheckedChange={(checked) => setUseReference(checked)}
                  />
                  <Label htmlFor={referenceId} className="flex-1 text-sm">
                    {t('editor.coverImage.ai.useReference')}
                  </Label>
                </div>
                {useReference && (
                  <FrameSourcePicker
                    candidates={candidates}
                    selected={referenceSource}
                    onSelect={setChosenSource}
                    onRetry={retryCandidate}
                    exportAspect={exportAspect}
                    disabled={generating}
                    compact
                  />
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor={promptId} className="text-sm">
                {t('editor.coverImage.ai.promptLabel')}
              </Label>
              <Textarea
                id={promptId}
                value={prompt}
                maxLength={MAX_AI_COVER_PROMPT_LENGTH}
                rows={4}
                disabled={generating}
                placeholder={t('editor.coverImage.ai.promptPlaceholder')}
                onChange={(event) => setPrompt(event.target.value)}
              />
              <div className="space-y-1.5 pt-1">
                <p className="text-xs text-muted-foreground">{t('editor.coverImage.ai.presetsLabel')}</p>
                <div className="flex flex-wrap gap-1.5" data-testid="ai-cover-presets">
                  {AI_COVER_PROMPT_PRESETS.map((id) => {
                    const phrase = t(`editor.coverImage.ai.presets.${id}.prompt`);
                    const active = hasAiCoverPromptPreset(prompt, phrase);
                    return (
                      <Button
                        key={id}
                        type="button"
                        size="sm"
                        variant={active ? 'secondary' : 'outline'}
                        aria-pressed={active}
                        disabled={generating}
                        data-testid={`ai-cover-preset-${id}`}
                        className="h-7 rounded-full px-2.5 text-xs"
                        onClick={() =>
                          setPrompt((current) =>
                            toggleAiCoverPromptPreset(current, phrase, MAX_AI_COVER_PROMPT_LENGTH),
                          )
                        }
                      >
                        {active && <Check className="mr-1 h-3 w-3" aria-hidden="true" />}
                        {t(`editor.coverImage.ai.presets.${id}.label`)}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
            {generating ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t('editor.coverImage.ai.generating')}
              </p>
            ) : (
              <Button
                type="button"
                className="w-full"
                disabled={referencePending}
                onClick={() => void handleGenerate()}
              >
                <Sparkles className="mr-1.5 h-4 w-4" aria-hidden="true" />
                {t('editor.coverImage.ai.generate')}
              </Button>
            )}
          </div>
        )}

        {errorCode && !generating && (
          <p role="alert" className="text-sm text-destructive">
            {t(`editor.coverImage.ai.errors.${errorCode}`)}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
